import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { ApiService } from '@/_services/api.service';
import { useStreamingMessage } from '../streaming/useStreamingMessage';
import { clearInterruptAtom } from '../atoms/interruptAtoms';
import type { InterruptAnswer } from '@/types';

interface ResumeInterruptParams {
	conversationId: string;
	messageId: string;
	answer: InterruptAnswer;
}

/**
 * Mutation hook to resume an interrupted AI graph with the user's answer.
 *
 * Flow:
 * 1. Clear the interrupt UI state
 * 2. Start a new SSE stream via the resume endpoint
 * 3. stream_end updates the interrupted assistant message in cache
 */
export const useResumeInterrupt = () => {
	const { startMessageStream } = useStreamingMessage();
	const clearInterrupt = useSetAtom(clearInterruptAtom);

	return useMutation({
		mutationFn: async (params: ResumeInterruptParams) => params,
		onSuccess: ({ conversationId, messageId, answer }) => {
			clearInterrupt();

			startMessageStream(async (signal) => ApiService.ResumeInterrupt({ conversationId, messageId, answer, signal }), {
				conversationId,
				skipMessageEvents: true,
				skipStreamStart: true
			});
		}
	});
};
